import { useState, useCallback } from "react";
import { Modal } from "@/shared/components/Modal";
import { Button } from "@/shared/components/form/Button";
import { NewConfirmationForm } from "./NewConfirmationForm";
import type { NewConfirmationFormData } from "../types/new-confirmation.types";
import {
  validateNewConfirmationForm,
  translateNewConfirmationErrors,
  type NewConfirmationFormErrors,
} from "../utils/validateNewConfirmationForm";
import useTranslations from "@/shared/lang/useTranslations";

const getDefaultFormData = (): NewConfirmationFormData => {
  const today = new Date();
  const fecha =
    today.getFullYear() +
    "-" +
    String(today.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(today.getDate()).padStart(2, "0");
  return {
    cliente: "",
    clienteId: null,
    pagadorEsMismoCliente: false,
    nombrePagador: "",
    pagadorId: null,
    monto: "",
    tipoMoneda: "",
    banco: "",
    fecha,
    nota: "",
    comprobante: null,
  };
};

type NewConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: NewConfirmationFormData) => void;
};

export const NewConfirmationModal = ({
  isOpen,
  onClose,
  onSubmit,
}: NewConfirmationModalProps) => {
  const { t } = useTranslations("confirmations");
  const [formData, setFormData] = useState<NewConfirmationFormData>(getDefaultFormData);
  const [errors, setErrors] = useState<NewConfirmationFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setFormData(getDefaultFormData());
      setErrors({});
      onClose();
    }
  }, [onClose, isSubmitting]);

  const handleSubmit = useCallback(async () => {
    const validationErrors = validateNewConfirmationForm(formData, {
      requireAmountAndBank: false,
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(translateNewConfirmationErrors(t, validationErrors));
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    try {
      await onSubmit?.(formData);
      setFormData(getDefaultFormData());
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onSubmit, onClose, t]);

  const footer = (
    <div className="flex flex-col space-y-4 w-full pt-1">
      <Button
        type="button"
        variant="primary"
        onClick={handleSubmit}
        disabled={isSubmitting}
        isLoading={isSubmitting}
        className="w-full py-3.5 rounded-xl font-bold shadow-lg shadow-green-500/20 active:scale-[0.98]"
      >
        {t("modal_new_submit")}
      </Button>
      <button
        type="button"
        onClick={handleClose}
        disabled={isSubmitting}
        className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 text-sm font-medium transition-colors"
      >
        {t("modal_new_cancel")}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("modal_new_title")}
      size="md-wide"
      footer={footer}
    >
      <NewConfirmationForm
        value={formData}
        onChange={setFormData}
        errors={errors}
        disabled={isSubmitting}
        hideAmountAndBank
        showClientAndPayerFields={false}
      />
    </Modal>
  );
};
